/**
 * Статусы присутствия: подписи, свой статус из настроек и то, что показывается под именем.
 * Выбранный статус живёт на сервере (PATCH /users/@me/settings) и приходит в READY,
 * поэтому переживает переподключения; «неактивен» после 10 минут без ввода — забота клиента.
 */
import { authed } from './auth'
import type { PresenceActivity, PresenceStatus, SelfStatus, UserSettings } from '@/types/api'

export const STATUS_LABEL: Record<PresenceStatus, string> = {
  online: 'В сети',
  idle: 'Неактивен',
  dnd: 'Не беспокоить',
  invisible: 'Невидимый',
  offline: 'Не в сети',
}

/** Что можно выбрать в меню статуса — в порядке Discord. */
export const SELF_STATUSES: SelfStatus[] = ['online', 'idle', 'dnd', 'invisible']

/** Подсказка под пунктом меню — там, где статус меняет что-то кроме значка. */
export const STATUS_HINT: Partial<Record<SelfStatus, string>> = {
  dnd: 'Уведомления не приходят',
  invisible: 'Для остальных вы не в сети',
}

/** Длина своего статуса, как у Discord и на сервере. */
export const MAX_CUSTOM_STATUS = 128

export const settingsApi = {
  update: (patch: { status?: SelfStatus; custom_status?: { text: string; expires_at?: string | null } | null; show_current_game?: boolean }) =>
    authed<UserSettings>('/users/@me/settings', { method: 'PATCH', body: JSON.stringify(patch) }),
}

/** Виден ли человек в сети: неактивный и «не беспокоить» — тоже в сети. */
export function isOnline(status: PresenceStatus | undefined): status is 'online' | 'idle' | 'dnd' {
  return status === 'online' || status === 'idle' || status === 'dnd'
}

/** Свой статус из активностей присутствия: type 4, текст в state. */
export function customStatusOf(activities: PresenceActivity[] | undefined): string {
  return activities?.find((a) => a.type === 4)?.state?.trim() ?? ''
}

const VERB: Record<number, string> = { 0: 'Играет в', 1: 'Стримит', 2: 'Слушает', 3: 'Смотрит', 5: 'Соревнуется в' }

/** Чем человек занят, кроме своего статуса: игра или активность бота («Слушает радио»). */
export function activityOf(activities: PresenceActivity[] | undefined): { type: number; name: string; label: string } | null {
  const a = activities?.find((x) => x.type !== 4 && VERB[x.type] && x.name)
  return a ? { type: a.type, name: a.name, label: `${VERB[a.type]} ${a.name}` } : null
}
